import {useMemo} from "react";
import useInput from "@/app/_components/forms/useInput";
import useDynamicListField from "@/app/_components/forms/useDynamicListField";
import useCheckboxGroup from "@/app/_components/forms/useCheckboxGroup";
import {
	ContactFormSelectedStudents,
	ContactFormStudentHandlers,
} from "./contactFormUtils";
import useCheckboxWithInputToggleGroup from "./useCheckboxWithInputToggleGroup";

export type UseContactFormStudentFields = ContactFormSelectedStudents &
	ContactFormStudentHandlers;

const useContactFormStudentFields: () => UseContactFormStudentFields = () => {
	const [
		initialStudentName,
		{
			onInputChange: onInitialStudentChange,
			setInputValue: setInitialStudentName,
		},
	] = useInput();
	const [
		newStudentNames,
		{
			setItem: setNewStudent,
			setItems: setNewStudents,
			resetItems: resetNewStudents,
		},
	] = useDynamicListField();
	const [
		selectedAcademyStudents,
		{
			setSelectedItems: setSelectedAcademyStudents,
			resetItems: resetSelectedAcademyStudents,
		},
	] = useCheckboxGroup();
	const {
		checkedItems: selectedPlatformStudents,
		inputValuesByCheckboxId: platformStudentNamesById,
		setCheckedItems: setSelectedPlatformStudents,
		onInputFieldChange: onPlatformStudentNameChange,
		clearItems: clearSelectedPlatformStudents,
	} = useCheckboxWithInputToggleGroup({isEmptyFieldOmitted: false});

	const handlers = useMemo(() => {
		return {
			onInitialStudentChange,
			setNewStudent,
			setNewStudents,
			setSelectedAcademyStudents,
			setSelectedPlatformStudents,
			onPlatformStudentNameChange,
			resetStudentFields: () => {
				setInitialStudentName("");
				resetNewStudents();
				resetSelectedAcademyStudents();
				clearSelectedPlatformStudents();
			},
		};
	}, [
		onInitialStudentChange,
		setNewStudent,
		setNewStudents,
		setSelectedAcademyStudents,
		setSelectedPlatformStudents,
		onPlatformStudentNameChange,
		setInitialStudentName,
		resetNewStudents,
		resetSelectedAcademyStudents,
		clearSelectedPlatformStudents,
	]);

	return {
		initialStudentName,
		newStudentNames,
		selectedAcademyStudents,
		selectedPlatformStudents,
		platformStudentNames: platformStudentNamesById,
		...handlers,
	};
};

export default useContactFormStudentFields;
